import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Locale, LocalizedString } from "@/data/content";

interface LanguageContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (content: LocalizedString) => string;
  switchLanguage: () => void;
  getLocalizedHref: (enHref: string, esHref?: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  const getLocaleFromPath = (pathname: string): Locale => {
    return pathname.startsWith("/es") ? "es" : "en";
  };

  const [locale, setLocaleState] = useState<Locale>(() => {
    const pathLocale = getLocaleFromPath(location.pathname);
    if (pathLocale === "es") return "es";
    const saved = localStorage.getItem("martinez-locale") as Locale | null;
    return saved || "en";
  });

  useEffect(() => {
    const pathLocale = getLocaleFromPath(location.pathname);
    if (pathLocale !== locale) {
      setLocaleState(pathLocale);
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem("martinez-locale", locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    const currentPath = location.pathname;
    if (newLocale === "es" && !currentPath.startsWith("/es")) {
      navigate("/es" + (currentPath === "/" ? "" : currentPath));
    } else if (newLocale === "en" && currentPath.startsWith("/es")) {
      const enPath = currentPath.replace(/^\/es/, "") || "/";
      navigate(enPath);
    }
  }, [location.pathname, navigate]);

  const switchLanguage = useCallback(() => {
    setLocale(locale === "en" ? "es" : "en");
  }, [locale, setLocale]);

  const t = useCallback((content: LocalizedString): string => {
    return content[locale] || content.en;
  }, [locale]);

  const getLocalizedHref = useCallback((enHref: string, esHref?: string): string => {
    if (locale === "es") {
      return esHref || "/es" + (enHref === "/" ? "" : enHref);
    }
    return enHref;
  }, [locale]);

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t, switchLanguage, getLocalizedHref }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLocale() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLocale must be used within a LanguageProvider");
  }
  return context;
}
